import React, { useState } from 'react';
import { UIHelper, ValidationHelper } from '../../utils/crudHelpers';

// Form field interface
export interface FormField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'select' | 'textarea' | 'checkbox';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  validation?: (value: any) => string | null;
  width?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

// Form props interface
interface StandardFormProps {
  fields: FormField[];
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  submitLabel?: string;
  submitIcon?: string;
  onCancel?: () => void;
  cancelLabel?: string;
  loading?: boolean;
  title?: string;
}

export function StandardForm({
  fields,
  initialData = {},
  onSubmit,
  submitLabel = 'Guardar',
  submitIcon = '',
  onCancel,
  cancelLabel = 'Cancelar',
  loading = false,
  title
}: StandardFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));

    // Clear error when user starts typing
    if (errors[fieldKey]) {
      setErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      const value = formData[field.key];

      // Required validation
      if (field.required) {
        const error = ValidationHelper.required(value, field.label);
        if (error) {
          newErrors[field.key] = error;
          return;
        }
      }

      // Custom validation
      if (field.validation && value !== undefined && value !== '') {
        const error = field.validation(value);
        if (error) {
          newErrors[field.key] = error;
          return;
        }
      }

      // Type-specific validation
      if (value !== undefined && value !== '') {
        switch (field.type) {
          case 'email':
            const emailError = ValidationHelper.email(value);
            if (emailError) newErrors[field.key] = emailError;
            break;
          case 'number':
            const numericError = ValidationHelper.numeric(value, field.label);
            if (numericError) newErrors[field.key] = numericError;
            break;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Reset form on successful submission
      setFormData(initialData);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Error al guardar. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const inputStyle = {
      ...UIHelper.getStandardInputStyle(),
      width: field.width || 'auto'
    };

    const hasError = !!errors[field.key];
    const errorStyle = hasError ? { borderColor: '#f44336' } : {};

    const value = formData[field.key] || (field.type === 'number' ? 0 : '');

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            style={{ ...inputStyle, ...errorStyle }}
            disabled={field.disabled || loading}
            required={field.required}
          >
            <option value="">{field.placeholder || `Seleccionar ${field.label}`}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            style={{
              ...inputStyle,
              ...errorStyle,
              minHeight: '80px',
              resize: 'vertical' as const
            }}
            disabled={field.disabled || loading}
            required={field.required}
          />
        );

      case 'checkbox':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              disabled={field.disabled || loading}
              style={{ margin: 0 }}
            />
            {field.placeholder || field.label}
          </label>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.key, Number(e.target.value))}
            placeholder={field.placeholder}
            style={{ ...inputStyle, ...errorStyle }}
            disabled={field.disabled || loading}
            required={field.required}
            min={field.min}
            max={field.max}
            step={field.step}
          />
        );

      default: // text, email
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            style={{ ...inputStyle, ...errorStyle }}
            disabled={field.disabled || loading}
            required={field.required}
          />
        );
    }
  };

  return (
    <div style={UIHelper.getStandardCardStyle()}>
      {title && <h3>{title}</h3>}

      <form onSubmit={handleSubmit}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          {fields.map(field => (
            <div key={field.key}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '500',
                color: '#333'
              }}>
                {field.label}
                {field.required && <span style={{ color: '#f44336' }}>*</span>}
              </label>

              {renderField(field)}

              {errors[field.key] && (
                <div style={{
                  color: '#f44336',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  {errors[field.key]}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading || isSubmitting}
              style={UIHelper.getStandardButtonStyle('secondary')}
            >
              {cancelLabel}
            </button>
          )}

          <button
            type="submit"
            disabled={loading || isSubmitting}
            style={{
              ...UIHelper.getStandardButtonStyle('success'),
              opacity: (loading || isSubmitting) ? 0.6 : 1
            }}
          >
            {submitIcon && `${submitIcon} `}
            {isSubmitting ? 'Guardando...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}